import { ALEXHARRI_GLSL } from './alexharri';
import { MAX_DISTURBANCES } from './field';

/**
 * 背景的两个着色器阶段。
 *
 * 评审描述的流水线是**两步**，所以这里就真的是两个 pass、两个 `Filter`：
 *
 * ```
 *   ① 标量场 → 叠加 → 非线性映射 → 灰阶     ScalarFieldFilter
 *   ② 灰阶 → 半调网点                        HalftoneFilter
 * ```
 *
 * 拆成两步而不是塞进一个着色器，理由是**能单独关掉某一级来看**：
 * 调半调参数时把①留着、把②关掉，就能判断"这个斑点不对"到底是场的错
 * 还是网点的错。多一个全屏 pass 对这个尺寸的背景完全无所谓。
 *
 * ## 关于坐标系
 *
 * 两个阶段都从 `vQuadCoord`（**滤镜四边形自己的 [0,1] 坐标**）出发，靠
 * `uQuad` 换算成**画布逻辑坐标**（画布永远 1280×800，见 `app.ts`）。
 *
 * ```
 *   logical = uQuad.xy + vQuadCoord * uQuad.zw      // uQuad = 四边形在画布上的矩形
 * ```
 *
 * ⚠️ **为什么不能直接写 `vQuadCoord * uResolution`**（那是第一版）：
 * 滤镜的四边形是 Pixi 按**纹理池**分配的那张贴图决定的 —— 它可能比申请的
 * 区域**大**（下一个 2 的幂，或者整块屏幕）。窗口不是 16:10 时申请的区域
 * 只是画布那一块（1280×800 缩放后），而池子给的贴图更大，于是
 * `vQuadCoord` 铺满的是那块**更大的**区域：场被拉伸、`uMask` 的采样坐标
 * 跟着错位（卡牌底下挖的洞跑到别处去了），而且场会**溢出到黑边**上去 ——
 * 表现就是"左边和上边空一块、右边和下边多一块"。
 *
 * 所以四边形到底有多大，必须由**调用方**用 `uQuad` 明说。
 * 默认值 `(0, 0, resolution)` 恰好等于旧行为（`vQuadCoord * uResolution`），
 * 于是调试页、濒死阴影那些"四边形就等于贴图"的场合一个字都不用改。
 *
 * ⚠️ `vTextureCoord` 仍然是**采样输入贴图**要用的那个坐标，两个别搞混：
 * 采样用 `vTextureCoord`，算位置用 `vQuadCoord` + `uQuad`。
 */

/**
 * 阶段一：标量场。
 *
 * 每个像素上：
 *   1. **初始状态**：alexharri 那套渐变背景在**这一时刻**的明度（`alexharri.ts`）
 *   2. 把 N 条扰动各自的贡献算出来**加在它上面**
 *   3. 走一遍**基于参数的非线性映射**得到灰阶
 *
 * ⚠️ 场是**逐像素现算**的，CPU 一个像素都不算 —— 它只生产扰动的参数
 * （`field.ts` 打包成 uniform）。所以"完全在 GPU 内计算"这件事一直成立，
 * 见 `docs/background-shader.md` §1。
 *
 * ⚠️ 循环边界必须是**编译期常量**（`MAX_DISTURBANCES`），不能用 `uCount`
 * 当上界 —— GLSL ES 1.00 不允许非常量循环边界。所以跳过无效条目靠
 * `amp > 0` 判断（CPU 打包时把空槽位填 0）。
 */
export const SCALAR_FIELD_FRAGMENT = /* glsl */ `
in vec2 vTextureCoord;
in vec2 vQuadCoord;
out vec4 finalColor;

uniform float uTime;
uniform vec2 uResolution;
/**
 * **初始状态那一层的参数**（alexharri 的渐变背景，见 alexharri.ts）。
 *
 * - uBaseAmp：围绕 0.5 放大多少。1 = 原版；0 = 一整片平的中灰
 * - uBaseBias：整体抬升 / 压低
 * - uBaseScale：空间频率倍率（越大波带越碎）
 * - uBaseSpeed：时间倍率（在 FIELD_TIME_SCALE 之上再乘一道）
 */
uniform float uBaseAmp;
uniform float uBaseBias;
uniform float uBaseScale;
uniform float uBaseSpeed;
/**
 * **滤镜四边形在画布逻辑坐标里的矩形** (x, y, w, h)。
 *
 * 窗口不是 16:10 时它比画布大（场要铺满整块屏幕），所以 x/y 常常是负数。
 * 见文件头"关于坐标系"。
 */
uniform vec4 uQuad;

// 卡牌占地遮罩。被卡牌盖住的地方，整片场**置零**
uniform sampler2D uMask;
uniform float uMaskStrength;
/**
 * **濒死裂缝遮罩**。和卡牌遮罩一样是"哪里不许有场"，只是来源不同 ——
 * 它是从画布正中放射出去的那些碎裂线条（见 render/dying/cracks.ts）。
 *
 * ⚠️ 两张遮罩**取并集**：被任意一张盖住的地方都置零。分成两张贴图而不是
 * 合成一张，是因为它们的生命周期、分辨率和更新频率都不一样（卡牌每帧动，
 * 裂缝只在濒死那几秒长出来）
 */
uniform sampler2D uCrack;
uniform float uCrackStrength;

// 扰动数组。三个数组一一对应 Disturbance 的三组参数
uniform vec4 uDistA[${MAX_DISTURBANCES}];   // (kind, x, y, bornAt)
uniform vec4 uDistB[${MAX_DISTURBANCES}];   // (amp, freq, speed, life)
uniform vec4 uDistC[${MAX_DISTURBANCES}];   // (radius, phase, -, -)

// 非线性映射：gain / gamma / contrast / bias
uniform vec4 uTone;
// 值的窗口：低于 lo 全黑（= 完全没有点）、高于 hi 全白（= 实心）
uniform vec2 uWindow;

${ALEXHARRI_GLSL}

// ------------------------------------------------------------
// 四种扰动算法。参数一律显式传进来，不用打包的 vec —— 免得读代码时
// 还要回去查"P.z 到底是哪个量"
// ------------------------------------------------------------

// 水波纹：一个向外扩散的波包。波前走到哪儿，环就到哪儿
float fRipple(float r, float age, float freq, float speed, float radius, float phase) {
  float front = speed * age;
  float ring = exp(-pow((r - front) / max(radius, 1.0), 2.0));
  return sin(freq * (r - front) + phase) * ring;
}

// 涡旋：角向正弦 × 半径高斯包络。像一圈慢慢拧起来的漩涡
float fSwirl(vec2 d, float r, float age, float freq, float speed, float radius, float phase) {
  float ang = atan(d.y, d.x);
  return sin(freq * ang + age * speed + phase) * exp(-pow(r / max(radius, 1.0), 2.0));
}

// 脉动：从中心往外推的同心驻波，半径上指数衰减
float fPulse(float r, float age, float freq, float speed, float radius, float phase) {
  return sin(freq * r - age * speed + phase) * exp(-r / max(radius, 1.0));
}

// 行波：固定方向扫过整个背景。phase 同时是初相和方向
float fWave(vec2 p, float age, float freq, float speed, float phase) {
  vec2 dir = vec2(cos(phase), sin(phase));
  return sin(freq * dot(p, dir) - age * speed + phase);
}

// ------------------------------------------------------------
// 非线性映射
// ------------------------------------------------------------
float toneMap(float v) {
  v = v * uTone.x + uTone.w;                    // gain + bias
  v = clamp(v, 0.0, 1.0);
  v = pow(v, max(uTone.y, 0.0001));             // gamma
  v = (v - 0.5) * uTone.z + 0.5;                // contrast（绕中灰转）
  v = clamp(v, 0.0, 1.0);
  // 窗口：把窄窄一段拉到满量程。这是"画面有层次"最关键的一步
  return smoothstep(uWindow.x, uWindow.y, v);
}

void main() {
  // 画布逻辑坐标（0..1280 / 0..800 是画布本身，外面是溢出的那圈）
  vec2 logical = uQuad.xy + vQuadCoord * uQuad.zw;
  vec2 p = logical - uResolution * 0.5;        // 以画布中心为原点的逻辑坐标

  // ① **初始状态**：alexharri 那套渐变背景在**这一时刻**的明度。
  //    评审：「在所有的扰动之前，将 alexharri 的计算结果作为每一 t 值时标量场
  //    的初始状态」—— 所以它是场本身，不是"噪声项"：扰动加在它**上面**
  float sum = 0.5 + (ah_base(logical, uTime * uBaseSpeed) - 0.5) * uBaseAmp + uBaseBias;

  // ② 把每条扰动加起来
  for (int i = 0; i < ${MAX_DISTURBANCES}; i++) {
    vec4 A = uDistA[i];
    vec4 B = uDistB[i];
    vec4 C = uDistC[i];

    // 空槽位（CPU 填 0）和参数不合法的一律跳过。
    // ⚠️ 这里没有"条数"这个 uniform：循环上界必须是编译期常量，
    // 所以"这一条有没有效"只能靠参数本身判断（CPU 打包时空槽位全填 0）
    if (B.x <= 0.0 || B.w <= 0.0) continue;

    float age = uTime - A.w;
    if (age < 0.0 || age > B.w) continue;   // 还没出生 / 已经过期

    vec2 d = p - (A.yz - 0.5) * uResolution;
    float r = length(d);

    float v;
    if (A.x < 0.5) {
      v = fRipple(r, age, B.y, B.z, C.x, C.y);
    } else if (A.x < 1.5) {
      v = fSwirl(d, r, age, B.y, B.z, C.x, C.y);
    } else if (A.x < 2.5) {
      v = fPulse(r, age, B.y, B.z, C.x, C.y);
    } else {
      v = fWave(p, age, B.y, B.z, C.y);
    }

    // 时间包络。
    // ⚠️ 这里有个坑：只用 exp(-age/life) 的话，到了寿命那一刻还剩 e^-1 ≈ 37%
    // 的振幅 —— 而 CPU 正好在那一刻把这条扰动回收，于是波纹会**"啪"地消失**。
    // 所以再乘一个收尾窗口，保证 age = life 时振幅**正好是 0**。
    // 判据必须和 CPU 的 isAlive() 完全一致（都是 age <= life）。
    float t = age / B.w;
    float env = exp(-3.0 * t) * (1.0 - smoothstep(0.8, 1.0, t));
    sum += v * B.x * env;
  }

  // ③ 非线性映射 → 灰阶。
  // ⚠️ 初始状态是 0..1 的明度，扰动是有正有负的偏移，加起来可能出界 ——
  // toneMap 自己会 clamp。剩下的交给 uWindow 决定"哪些地方出点"
  float gray = toneMap(sum);

  // ④ 卡牌占地置零。
  // 评审：*「使得被卡牌层覆盖的所有位置上标量场置零」* ——
  // 注意是**场**置零，不是"把点盖住"。所以放在映射之后：
  // 灰阶归零 = 半调也自然没有点，卡牌底下是干净的纯黑。
  //
  // ⚠️ 用乘以 (1 - mask) 而不是相减：遮罩是抗锯齿的（线性采样会给出
  // 0..1 的中间值），相减会在边缘留一圈半灰的脏边
  //
  // ⚠️ 采样坐标用**画布逻辑坐标归一化**（logical / uResolution），
  //    因为卡牌遮罩贴图是**画布尺寸**的。窗口比画布宽时 logical 会跑到
  //    画布外面去 —— 那里的 UV 越界，而越界采样（clamp）会把边缘那一列像素
  //    拉成一条长条。所以显式乘一个"在不在画布里"的方波
  vec2 maskUV = logical / uResolution;
  float inside =
    step(0.0, maskUV.x) * step(maskUV.x, 1.0) *
    step(0.0, maskUV.y) * step(maskUV.y, 1.0);
  float cardMask = texture(uMask, maskUV).a * uMaskStrength * inside;

  // 裂缝遮罩贴的是**整块视口**（uQuad），不是画布 —— 场铺到哪儿它就铺到哪儿。
  // 评审：「红线没有延伸至画面边缘」是同一个病的另一半：横带和裂缝都该跟着视口走，
  // 不然两边那几条黑边上会留着"没裂开的网点"，看着像贴图错位。
  vec2 crackUV = (logical - uQuad.xy) / max(uQuad.zw, vec2(1.0));
  float crackMask = texture(uCrack, crackUV).a * uCrackStrength;

  float mask = max(cardMask, crackMask);
  gray *= 1.0 - clamp(mask, 0.0, 1.0);

  finalColor = vec4(gray, gray, gray, 1.0);
}
`;

/**
 * 阶段二：半调。
 *
 * 把灰阶变成网点：**点的面积正比于灰度**，所以半径正比于 sqrt(灰度)。
 * 这是印刷半调的定义，也是它看起来"对"的原因 —— 直接拿灰度当半径，
 * 高光部分会挤成一片。
 *
 * 颜色只有两个：纸色和墨色。把纸色设成背景色，网点就"浮"在背景上；
 * 想让整块背景被网点盖住，纸色就设成一张纸的颜色。
 */
export const HALFTONE_FRAGMENT = /* glsl */ `
in vec2 vTextureCoord;
in vec2 vQuadCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform vec2 uResolution;
/** 四边形在画布逻辑坐标里的矩形。见文件头"关于坐标系" */
uniform vec4 uQuad;

// (dotPitch, angle, softness, contrast)
uniform vec4 uDot;
uniform vec3 uPaper;   // 纸色（没有点的地方）
uniform vec3 uInk;     // 墨色（点里）
/**
 * 0 = **不透明印刷**：整块画面都归这一级所有（纸色铺满），alpha 恒为 1。
 * 1 = **镂空**：网点当作遮罩用，alpha 跟着覆盖率走，网点之外是透明的。
 *
 * 背景要的是 0（它铺满整个画布）；濒死的黑色半调阴影要的是 1 ——
 * 那张阴影比牌大得多、还带着模糊的软边，出了形状的地方必须是**透明的**，
 * 不然会得到一个黑矩形。
 */
uniform float uAlphaFromInk;
/**
 * **情绪**：亮度倍率 + 色调倍率（bg/mood.ts 算出来的）。
 *
 * uMoodTint 乘在**颜色**上（暖橙 / 冷蓝 / Bar 那一下泛红）。
 *
 * ⚠️ uMoodBrightness **不再乘颜色**，它去乘**覆盖率**（见下面 coverage）——
 *    二值化之后把墨调暗只会得到灰点，和"不是黑就是白"直接冲突。
 *    默认 (1, 1, 1) / 1，所以别的用处（濒死阴影烘焙）一个字都不用改。
 */
uniform float uMoodBrightness;
uniform vec3 uMoodTint;
/**
 * **点的形状**：0 = 圆、1 = 方、2 = 菱形、3 = 十字、4 = 横线。
 *
 * 评审：「令点的形状可控」。它是 uDotShape 一个数，所以调试页一个滑块就够了，
 * 不用为每种形状各写一份着色器 —— 形状只影响"格子里的距离怎么算"。
 */
uniform float uDotShape;
/**
 * **网格线性放大几倍**（只有像素化档位 > 1）。
 *
 * 评审：「半调网格也跟着线性放大，不和像素化一样 2^x 放大」。所以倍率乘在
 * **节距**上（一格几像素），而不是乘在格子坐标上（那会把整块网点挪位）。
 */
uniform float uGridScale;
/**
 * **要不要画成 1-bit 硬边**（1 = 是，0 = 柔边）。
 *
 * 0 的时候走的是**加像素化之前那条路**（smoothstep 抗锯齿、不吸像素格、
 * 情绪亮度乘颜色）—— 见 renderScale.ts 的 FIELD_HALFTONE_BINARY。
 */
uniform float uBinary;
/**
 * **这一级往哪张表面上画**（那东西的像素尺寸）。像素网格对齐要用它。
 *
 * ⚠️ 两个都不能用：uInputSize 是**输入**贴图的尺寸，uOutputTexture 是
 *    Pixi 的：两者都来自**纹理池**，常常比实际区域**大一圈**（说好 640×400，
 *    拿到的可能是 1024×512），按它算"一个像素多大"就全错。
 *
 * ⚠️ 更要命的一条：**片元着色器里不许自己声明 uOutputTexture**。
 *    那个名字在 filterQuad.ts 的顶点着色器里已经声明过了，而 GLSL ES 1.00
 *    给两个阶段定的**默认精度不一样**（顶点 highp、片元 mediump）——
 *    同名 uniform 精度不一致，程序**链接失败**：Pixi 只打一句
 *    "Could not initialize shader"，然后整块背景纯黑。
 *    所以在片元这一侧只用我们自己传进来的 uTargetSize。
 *
 * 它由调用方给（Background 知道自己的 FBO 有多大）：
 * 于是"一个逻辑单位 = 几个像素"是一件 CPU 说了算的事实，不靠猜 Pixi 的池子。
 */
uniform vec2 uTargetSize;

/**
 * **格子里的归一化距离**（0 = 格心，1 = 格边中点）。
 *
 * 形状就是换一个度量：
 *
 * | 形状 | 度量 | 长出来是什么 |
 * | --- | --- | --- |
 * | 圆 | 欧氏 2*len | 经典的调幅网点 |
 * | 方 | 切比雪夫 2*max | 方块网点（像素风更硬） |
 * | 菱形 | 曼哈顿 2*(|x|+|y|) | 45° 的菱形，和斜杠一个调子 |
 * | 十字 | 2*min | 每个格子里一个 +，半径是**臂的半宽** |
 * | 横线 | 2*|y| | 一格格横线接起来 = 线屏 |
 *
 * ⚠️ 每个分支都乘了那个 2（= 边中点为 1）。漏了的表现是这一种形状在同样的
 *    灰度下比别的大一倍、点连成一片，看上去像"整块反白"。
 */
float cellDistance(vec2 cell, float shape) {
  vec2 a = abs(cell) * 2.0;
  if (shape < 0.5) return length(cell) * 2.0;
  if (shape < 1.5) return max(a.x, a.y);
  if (shape < 2.5) return a.x + a.y;
  if (shape < 3.5) return min(a.x, a.y);
  return a.y;
}

#define QUARTER_PI 0.7853981633974483

/**
 * **把"圆点的半径"换算成这种形状的半径。**
 *
 * 为什么需要这一步：五种形状的**面积律**完全不同 —— 同样的半径，正方形盖住的
 * 面积是圆的 1.27 倍、菱形只有 0.64 倍、十字能到 9 倍。直接换度量的话，
 * 评审拖一下滑块会看到画面**一会儿全黑一会儿全白**，那就不是"形状可控"，
 * 而是"密度失控"。
 *
 * 所以口径定成：**半径一律按"圆点的半径"给，每一种形状自己把它换算成
 * 等面积的半径**。于是滑块只改形状，印刷的深浅一动不动。
 *
 * | 形状 | 覆盖率（半径 r） | 反解 |
 * | --- | --- | --- |
 * | 圆 | π r² / 4 | （基准，原样返回） |
 * | 方 | r² | r = √(π r²/4) |
 * | 菱形 | r²/2（r ≤ 1）；1 − 2(1 − r/2)²（r > 1） | 见下面那段：**分段** |
 * | 十字 | 1 − (1 − r)² | r = 1 − √(1 − π r²/4) |
 * | 横线 | r | r = π r² / 4 |
 *
 * ⚠️ 圆那一条**原样返回**是故意的：背景现在的观感（0.95 那个系数、
 *    "满分时刚好连成一片实心"）全是按圆调的，换算不能碰它。
 *
 * ⚠️ 十字和横线**是非线性的**（它们的"半径"一个是臂的半宽、一个是线宽），
 *    所以不能只乘一个常数 —— 乘常数只在小半径下近似，半径一大就偏了。
 *
 * ⚠️ 菱形是**分段**的：顶点长过格边（r > 1）之后会被格子裁掉四个角，
 *    面积律从 r²/2 变成 1 − 2(1 − r/2)²。不分段的话，覆盖率一过半格
 *    菱形就比别的形状小一截（0.79 的灰度下差 6 个百分点）。
 */
float shapeRadius(float r, float shape) {
  float area = QUARTER_PI * r * r;
  if (shape < 0.5) return r;
  if (shape < 1.5) return sqrt(area);
  if (shape < 2.5) {
    if (area < 0.5) return sqrt(2.0 * area);
    return 2.0 - sqrt(2.0 * max(0.0, 1.0 - area));
  }
  if (shape < 3.5) return 1.0 - sqrt(max(0.0, 1.0 - area));
  return area;
}

void main() {
  float gray = texture(uTexture, vTextureCoord).r;

  // 网点图案在**逻辑画布坐标**上排布，所以要按位置算，
  // 不能用 vTextureCoord 直接当格子坐标（见文件头那段说明）
  //
  // ⚠️ 锚在**画布原点**（不是四边形原点）：窗口一变，四边形跟着动，
  //    拿它当格子原点的话整块网点会跟着窗口"游"起来
  vec2 p = uQuad.xy + vQuadCoord * uQuad.zw;

  /**
   * ★ **先把采样点吸附到像素网格上。**
   *
   * 评审：「令背景的半调滤镜的网格和像素网格对齐。」
   *
   * 背景现在是渲染进 1/2^n 的小 FBO 再邻近采样放大的，所以"一个像素"指的是
   * **FBO 的像素**。把 p 量化到像素中心再算图案之后：
   *
   * - 每个 FBO 像素只采样一次图案 → 网点**不可能有半覆盖的边**（和上一级的
   *   二值化是同一条取向，这里是它的另一半）
   * - 网点的格子对像素网格是**固定**的：不会出现"这一格的边落在半个像素上，
   *   于是相邻两格一个 2px 一个 3px"那种忽胖忽瘦
   * - 结果就是"1-bit 印刷品在低分辨率下被放大"该有的样子
   *
   * ⚠️ 锚点必须是**四边形原点**（= 目标表面的像素原点），不是画布原点：
   *    窗口不是 16:10 时 uQuad.xy 是负数（黑边那一段），按画布原点吸附会和
   *    真实像素格错开半格。
   *
   * ⚠️ uTargetSize 是"目标表面"的像素数，uQuad 还是画布逻辑坐标那一块 ——
   *    两者的比就是"一个逻辑单位等于几个像素"。
   */
  vec2 unitsPerPx = uQuad.zw / max(uTargetSize, vec2(1.0));
  /**
   * ⚠️ **吸附只在 1-bit 那一档做。** 柔边那一档是"加像素化之前的样子"：
   *    采样点不吸像素格（snapped = p），抗锯齿的柔边才不会被量化成台阶。
   */
  vec2 snapped = uBinary > 0.5
    ? uQuad.xy + (floor((p - uQuad.xy) / unitsPerPx) + 0.5) * unitsPerPx
    : p;

  float ca = cos(uDot.y);
  float sa = sin(uDot.y);
  vec2 rp = mat2(ca, -sa, sa, ca) * snapped;

  /**
   * ⚠️ 节距也吸附到**整数像素**：pitch 是逻辑单位，而格子得落在像素格上。
   *    这样"一格里有多少像素"是整数，点在放大之后才是规整的方块。
   *
   * ⚠️ uGridScale 乘在**节距**上（评审要的"网格也跟着线性放大"）：
   *    n=1 是设计尺寸 11 逻辑像素，n=2 是 22，n=3 是 33 —— 线性，不是 2^n。
   */
  float pxPitch = max(1.0, floor(uDot.x * uGridScale / unitsPerPx.x + 0.5));
  float pitch = uBinary > 0.5 ? pxPitch * unitsPerPx.x : max(uDot.x * uGridScale, 1.0);
  vec2 cell = fract(rp / pitch) - 0.5;
  float dist = cellDistance(cell, uDotShape);

  /**
   * 面积 ∝ 灰度 ⇒ 半径 ∝ sqrt(灰度)。0.95 让满分时刚好连成一片实心。
   *
   * ★ **情绪里的"亮度"在这里变成"覆盖率"**（评审要二值化，见下）：原来是拿
   *   uMoodBrightness 乘**颜色**（把墨调暗）——那会直接产出灰点，和"不是黑就是白"
   *   冲突。改成乘半径之后，"暗" = 点更小更少，屏幕上的像素仍然只有两级。
   *   （覆盖率 ∝ 半径²，所以视觉上的明暗变化依旧明显。）
   *
   * ★ **半径的口径是"圆点的半径"**：shapeRadius 再把它换成当前形状的半径，
   *   于是拖"点的形状"滑块只改形状、不改印刷深浅。见 shapeRadius 的说明。
   */
  /**
   * ⚠️ **亮度是"面积"倍率，所以半径要开方。**
   *
   * 直接拿亮度当半径倍率的话，半径 ∝ 亮度 ⇒ 面积 ∝ 亮度² —— 亮度一变两倍，
   * 点的面积就变四倍，看上去是"背景突然长大了"（濒死时情绪从 0.14 抬到 0.26
   * 就足够触发）。开方之后面积 ∝ 亮度，和这一级本来就用的"面积 ∝ 灰度"
   * 是同一条定律。
   */
  float coverage = uBinary > 0.5 ? sqrt(max(uMoodBrightness, 0.0)) : 1.0;
  float radius = shapeRadius(
    min(1.0, sqrt(clamp(gray * uDot.w, 0.0, 1.0)) * 0.95 * coverage),
    uDotShape
  );

  /**
   * ★ **二值化：一个像素要么全墨、要么全空。**
   *
   * 评审：「仅是临近采样有点丑，我还需要二值化，最终呈现在屏幕上不是黑就是白。」
   *
   * 做在**这里**（而不是再加一道后处理）的理由：这一级的输出会被渲染进那张
   * 1/2^n 的小 FBO，再邻近采样放大 —— 如果这里还留着 smoothstep 的灰边，
   * 放大之后每一道边都变成一大块灰，那正是"丑"的来源。在这里一步到位之后，
   * FBO 里只有 0/1，放大出来的就是硬邦邦的黑白方块。
   *
   * ⚠️ uDot.z（边缘柔度）在这条路上**没有用了** —— 二值化就是不要柔边。
   *    调试页里那个滑块已经删掉。
   */
  float ink = uBinary > 0.5
    ? step(dist, radius)
    // ⚠️ 柔边这一条**逐字抄自** 43089c0 那一版（加像素化之前）：
    //    抗锯齿宽度按格子大小归一，缩放时网点才不会闪
    : 1.0 - smoothstep(radius - max(uDot.z, 0.001) / pitch, radius + max(uDot.z, 0.001) / pitch, dist);

  float alpha = mix(1.0, ink, uAlphaFromInk);
  /**
   * 情绪：色调仍然是墨的颜色（暖橙 / 冷蓝 / Bar 那一下泛红）。
   *
   * ⚠️ **亮度只有 1-bit 那一档走覆盖率**（见上面的 coverage）：二值化之后
   *    把墨调暗会产出灰点，和"不是黑就是白"冲突。柔边那一档回到原来的做法 ——
   *    乘在**颜色**上（那时本来就有灰阶，压暗就是压暗）。
   */
  vec3 color = mix(uPaper, uInk, ink) * uMoodTint * (uBinary > 0.5 ? 1.0 : max(uMoodBrightness, 0.0));
  // ⚠️ 输出是**预乘**的：rgb 也要乘 alpha。不乘的话"镂空"模式下
  //    半覆盖的网点会被读成实心白（Pixi 的混合和 tint 都按预乘理解）
  finalColor = vec4(color * alpha, alpha);
}
`;
